// biome-ignore-all lint/security/noDangerouslySetInnerHtml: help text is admin-authored and always rendered through sanitizeRichText (via richTextDisplayHtml).
// =============================================================================
// HelpButton.jsx — info bubble that pops a contextual help dialog (read-only)
// =============================================================================
// Renders: a small ? icon. Clicking fetches help text from the `help_content`
//          table (keyed by helpKey) and shows it in a modal — view only.
//          Editing help content is done exclusively via Admin → Help Content
//          (HelpContentAdmin); there is no inline edit here by design
//          (Dev Team decision, 2026-06-12).
// Props:
//   helpKey - row key in help_content (e.g. "uniforms_supplier")
//   user    - current user object; the bubble only renders when signed in
//   light   - if true, render with a lighter icon style for dark backgrounds
// =============================================================================

import React from "react";
import { getHelpText } from "../../data/helpContent";
import { richTextDisplayHtml } from "../../services/richText";
import { ModalBackdrop } from "./FormUI.jsx";

function HelpButton({ helpKey, user, light, label }) {
  const [show, setShow] = React.useState(false);
  const [text, setText] = React.useState(null);

  // Reset the cached text whenever the key changes. This component is reused
  // across sections (the responder detail passes a DIFFERENT helpKey per selected
  // section — Stationery / Uniforms / Shoes / Device), and load() caches the text
  // after the first fetch; without this reset it would keep showing the FIRST
  // section's help for every other section.
  React.useEffect(
    function () {
      setText(null);
    },
    [helpKey]
  );

  async function load() {
    if (text !== null) {
      setShow(true);
      return;
    }
    var content = await getHelpText(helpKey);
    // Store the REAL content ("" when there is none). The friendly placeholder is
    // display-only (see below) — it is never persisted from here.
    var loaded = content?.trim() ? content : "";
    setText(loaded);
    setShow(true);
  }

  if (!user) return null;
  const onTrigger = function (e) {
    e.stopPropagation();
    load();
  };
  return (
    <span style={{ display: "inline-block" }}>
      {label ? (
        // Labeled variant — a bordered pill button matching the "Quick Start Guide"
        // button on the shelter dashboard (John, 23 Jul 2026), instead of the "?" bubble.
        <button
          onClick={onTrigger}
          className="bg-transparent rounded-[6px] py-[7px] px-[14px] text-[13px] cursor-pointer font-semibold text-zephyr-teal"
          style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
        >
          {label}
        </button>
      ) : (
        <button
          onClick={onTrigger}
          style={{
            background: "none",
            border: `1px solid ${light ? "rgba(255,255,255,0.7)" : "#00695C"}`,
            borderRadius: "50%",
            width: 20,
            height: 20,
            fontSize: 12,
            color: light ? "white" : "#00695C",
            cursor: "pointer",
            fontWeight: 700,
            lineHeight: "18px",
            padding: 0,
            marginLeft: 6,
            verticalAlign: "middle",
            opacity: 0.85,
          }}
        >
          ?
        </button>
      )}
      {show && (
        <ModalBackdrop
          portal
          onClose={function () {
            setShow(false);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 28,
            width: "calc(100vw - 40px)",
            maxWidth: 560,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            maxHeight: "80vh",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            textAlign: "left",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <strong style={{ color: "#00695C", fontSize: 16 }}>Help</strong>
            <button
              onClick={function () {
                setShow(false);
              }}
              style={{
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                color: "#666",
              }}
            >
              x
            </button>
          </div>
          <div style={{ textAlign: "left" }}>
            {text?.trim() ? (
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  color: "#333",
                  fontSize: 14,
                  lineHeight: 1.7,
                  margin: "0 0 12px",
                  maxWidth: "100%",
                  textAlign: "left",
                }}
                dangerouslySetInnerHTML={{ __html: richTextDisplayHtml(text) }}
              />
            ) : (
              <p
                style={{
                  color: "#333",
                  fontSize: 14,
                  lineHeight: 1.7,
                  margin: "0 0 12px",
                  textAlign: "left",
                }}
              >
                No help content yet.
              </p>
            )}
          </div>
        </ModalBackdrop>
      )}
    </span>
  );
}

export { HelpButton };
